import React, { useState, useEffect, useCallback } from 'react';
import { 
    Container, Typography, Box, Paper, Chip, IconButton, 
    Button, Tooltip, Alert, Dialog, 
    DialogTitle, DialogContent, DialogContentText, DialogActions, 
    Snackbar, TextField, FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { tenantConfig } from '../../config/tenantConfig'; // 🚨 Tenant Config Import

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const EmailQueuePage = () => {
    const { token, user } = useAuth();
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);

    // Role checks
    const isSuperUser = user?.role === 'SuperUser';
    const isFeatureEnabled = tenantConfig?.features?.showEmailQueue ?? true;

    // Filter States
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Modal & Toast States
    const [dialog, setDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const closeDialog = () => setDialog({ ...dialog, open: false });
    const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const fetchQueue = useCallback(async () => {
        if (!token || !isFeatureEnabled) return;
        setLoading(true);
        setPageError('');
        try {
            const res = await api.get('/queues/queue', { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            setQueue(res.data || []);
        } catch (err) {
            console.error("Failed to fetch email queue:", err);
            setPageError('Failed to load the email queue. Please check your connection.');
        } finally {
            setLoading(false);
        }
    }, [token, isFeatureEnabled]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    // --- Feature Disabled Check ---
    if (!isFeatureEnabled) {
        return (
            <Container maxWidth="md" sx={{ mt: 6 }}>
                <Alert severity="warning">
                    The Email Queue feature is not enabled for {tenantConfig.name}.
                </Alert>
            </Container>
        );
    }

    // --- Action Handlers ---
    const executeRetry = async (id) => {
        try {
            await api.put(`/queues/queue/${id}/retry`, {}, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: 'Email queued for retry.', severity: 'success' });
            fetchQueue();
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to retry email.', severity: 'error' });
        }
    };

    const confirmDelete = (id) => {
        if (!isSuperUser) return;
        setDialog({
            open: true,
            title: 'Confirm Deletion',
            message: 'Are you sure you want to permanently delete this email from the queue?',
            onConfirm: async () => {
                closeDialog();
                try {
                    await api.delete(`/queues/queue/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                    setSnackbar({ open: true, message: 'Email removed from queue.', severity: 'success' });
                    fetchQueue();
                } catch (err) {
                    setSnackbar({ open: true, message: 'Failed to delete email.', severity: 'error' });
                }
            }
        });
    };

    const confirmBulkRetry = () => {
        if (selectedRows.length === 0) return;
        setDialog({
            open: true,
            title: 'Confirm Bulk Retry',
            message: `Are you sure you want to requeue ${selectedRows.length} selected email(s)?`,
            onConfirm: async () => {
                closeDialog();
                setLoading(true);
                try {
                    await Promise.all(selectedRows.map(id => 
                        api.put(`/queues/queue/${id}/retry`, {}, { headers: { Authorization: `Bearer ${token}` } })
                    ));
                    setSelectedRows([]);
                    setSnackbar({ open: true, message: 'Emails successfully requeued.', severity: 'success' });
                    fetchQueue();
                } catch (err) {
                    setSnackbar({ open: true, message: 'Some emails failed to requeue.', severity: 'error' });
                    fetchQueue();
                }
            }
        });
    };

    const confirmBulkDelete = () => {
        if (!isSuperUser || selectedRows.length === 0) return;
        setDialog({
            open: true,
            title: 'Confirm Bulk Deletion',
            message: `Are you sure you want to permanently delete ${selectedRows.length} selected email(s) from the queue? This cannot be undone.`,
            onConfirm: async () => {
                closeDialog();
                setLoading(true);
                try {
                    await api.post('/queues/queue/bulk-delete', { ids: selectedRows }, { headers: { Authorization: `Bearer ${token}` } });
                    setSelectedRows([]);
                    setSnackbar({ open: true, message: 'Emails successfully deleted.', severity: 'success' });
                    fetchQueue();
                } catch (err) {
                    setSnackbar({ open: true, message: 'Failed to delete some emails.', severity: 'error' });
                    fetchQueue();
                }
            }
        });
    };

    const getStatusChipColor = (status) => {
        switch (status) {
            case 'Sent': return 'success';
            case 'Failed': return 'error';
            case 'Processing': return 'info';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    const filteredQueue = queue.filter((row) => {
        const matchesStatus = statusFilter === 'All' || row.Status === statusFilter;
        const searchLower = searchText.toLowerCase();
        const matchesSearch = 
            searchText === '' || 
            (row.RecipientEmail && row.RecipientEmail.toLowerCase().includes(searchLower)) ||
            (row.Subject && row.Subject.toLowerCase().includes(searchLower)) ||
            (row.FullName && row.FullName.toLowerCase().includes(searchLower)) ||
            (row.subject && row.subject.toLowerCase().includes(searchLower));

        return matchesStatus && matchesSearch;
    });

    const columns = [
        { field: 'Id', headerName: 'ID', width: 70 },
        { 
            field: 'FullName', 
            headerName: 'User Name', 
            flex: 1, 
            minWidth: 150,
            valueGetter: (params) => {
                if (!params || !params.row) return '-';
                return params.row.FullName || params.row.UserName || params.row.FirstNames || '-';
            }
        },
        { field: 'RecipientEmail', headerName: 'Recipient', flex: 1, minWidth: 200 },
        { 
            field: 'Subject', 
            headerName: 'Subject', 
            flex: 1, 
            minWidth: 200,
            valueGetter: (params) => {
                if (!params || !params.row) return 'No Subject';
                return params.row.Subject || params.row.subject || params.row.SUBJECT || '-';
            }
        },
        { 
            field: 'Status', 
            headerName: 'Status', 
            width: 130,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    color={getStatusChipColor(params.value)} 
                    size="small" 
                    variant={params.value === 'Pending' ? 'outlined' : 'filled'}
                />
            )
        },
        { 
            field: 'ScheduledFor', 
            headerName: 'Scheduled For', 
            width: 180, 
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '-' 
        },
        { 
            field: 'ErrorMessage', 
            headerName: 'Error Log', 
            flex: 1, 
            minWidth: 250,
            renderCell: (params) => {
                if (!params.value) return params.row.Status === 'Sent' ? <CheckCircleIcon color="success" fontSize="small" /> : '-';
                return (
                    <Tooltip title={params.value} placement="top" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontSize: '0.85rem' }}>
                            <ErrorOutlineIcon fontSize="small" />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                                {params.value}
                            </span>
                        </Box>
                    </Tooltip>
                );
            }
        },
        {
            field: 'actions', 
            headerName: 'Actions', 
            width: isSuperUser ? 120 : 80, 
            sortable: false,
            renderCell: (params) => {
                const canRetry = params.row.Status === 'Failed' || params.row.Status === 'Pending';
                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Retry / Requeue">
                            <span>
                                <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => executeRetry(params.row.Id)}
                                    disabled={!canRetry}
                                >
                                    <ReplayIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* 🚨 RESTRICTED TO SUPERUSER ONLY */}
                        {isSuperUser && (
                            <Tooltip title="Delete from Queue">
                                <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => confirmDelete(params.row.Id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        Outbound Email Queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Monitor, troubleshoot, and control bulk email dispatches.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {selectedRows.length > 0 && (
                        <>
                            {/* 🚨 BULK DELETE RESTRICTED TO SUPERUSER */}
                            {isSuperUser && (
                                <Button 
                                    variant="contained" 
                                    color="error" 
                                    startIcon={<DeleteIcon />} 
                                    onClick={confirmBulkDelete}
                                >
                                    Delete {selectedRows.length}
                                </Button>
                            )}
                            <Button 
                                variant="contained" 
                                color="warning" 
                                startIcon={<ReplayIcon />} 
                                onClick={confirmBulkRetry}
                            >
                                Retry {selectedRows.length}
                            </Button>
                        </>
                    )}
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchQueue}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 1 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Search by Email, Subject, or Name"
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Filter by Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="All">All Statuses</MenuItem>
                                <MenuItem value="Failed">Failed</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Processing">Processing</MenuItem>
                                <MenuItem value="Sent">Sent</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {pageError && <Alert severity="error" sx={{ mb: 3 }}>{pageError}</Alert>}

            <Paper sx={{ height: 650, width: '100%', boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
                <DataGrid 
                    rows={filteredQueue}
                    columns={columns} 
                    getRowId={(row) => row.Id} 
                    loading={loading}
                    checkboxSelection={isSuperUser} // 🚨 Only allow bulk selection for SuperUsers
                    disableSelectionOnClick
                    onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                    slots={{ toolbar: GridToolbar }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 50 } },
                        sorting: { sortModel: [{ field: 'ScheduledFor', sort: 'desc' }] }
                    }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Paper>

            <Dialog open={dialog.open} onClose={closeDialog}>
                <DialogTitle>{dialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialog.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="inherit">Cancel</Button>
                    <Button onClick={dialog.onConfirm} variant="contained" color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EmailQueuePage;
